/**
 * Nexera Plateforme Design System - Color Palette
 *
 * This file is the SINGLE SOURCE OF TRUTH for all colors in the application.
 * To change the color scheme, simply modify the values in `palette` below.
 *
 * Current Theme: Nexera Professional
 * A refined dark theme inspired by enterprise SaaS platforms.
 * Clean, minimal, with functional clarity and professional aesthetics.
 */

// =============================================================================
// EDITABLE COLOR PALETTE
// Change these values to update the entire application's color scheme
// =============================================================================

export const palette = {
  // Primary brand color - used for main actions, active states, highlights
  primary: '#3B82F6',           // Blue - professional and trustworthy
  primaryLight: '#60A5FA',      // Lighter blue for hover states
  primaryDark: '#2563EB',       // Darker blue for pressed states
  primaryMuted: 'rgba(59, 130, 246, 0.10)', // Subtle background tint

  // Secondary accent - used for highlights, badges, gradients
  accent: '#8B5CF6',            // Violet - modern and distinctive
  accentLight: '#A78BFA',       // Lighter violet
  accentDark: '#7C3AED',        // Darker violet

  // Tertiary color - for special elements
  tertiary: '#1E3A5F',          // Deep navy - sophisticated
  tertiaryLight: '#2563EB',     // Lighter navy
  tertiaryMuted: 'rgba(30, 58, 95, 0.12)',

  // Cyan accent - for info, links, secondary actions
  cyan: '#06B6D4',              // Cyan - for accents
  cyanLight: '#22D3EE',         // Lighter cyan
  cyanMuted: 'rgba(6, 182, 212, 0.10)',

  // ==========================================================================
  // DARK MODE COLORS (Primary theme)
  // ==========================================================================

  // Core backgrounds - layered for depth (darker, more neutral)
  background: '#09090B',        // Near-black background
  backgroundElevated: '#0F0F12', // Slightly elevated
  surface: '#18181B',           // Card/surface background (zinc-900)
  surfaceElevated: '#1F1F23',   // Elevated surfaces (modals, dropdowns)
  surfaceHover: '#27272A',      // Hover state for surfaces (zinc-800)
  surfaceBorder: '#2E2E33',     // Subtle surface borders

  // Sidebar specific
  sidebarBg: '#0C0C0E',         // Sidebar background
  sidebarHover: '#18181B',      // Sidebar item hover
  sidebarActive: 'rgba(59, 130, 246, 0.12)', // Active item background
  sidebarBorder: '#1F1F23',     // Sidebar border/dividers
  sidebarText: '#A1A1AA',       // Sidebar text (zinc-400)
  sidebarTextActive: '#FAFAFA', // Active sidebar text

  // Text colors
  textPrimary: '#FAFAFA',       // Primary text - high contrast (zinc-50)
  textSecondary: '#A1A1AA',     // Secondary text (zinc-400)
  textMuted: '#71717A',         // Muted/placeholder text (zinc-500)
  textOnPrimary: '#FFFFFF',     // Text on primary color
  textOnDark: '#FAFAFA',        // Text on dark backgrounds

  // Borders
  border: '#27272A',            // Standard border (zinc-800)
  borderLight: '#3F3F46',       // Lighter border for hover (zinc-700)
  borderFocus: '#3B82F6',       // Focus ring color

  // ==========================================================================
  // LIGHT MODE COLORS
  // ==========================================================================
  light: {
    background: '#FAFAFA',      // Light background (zinc-50)
    backgroundElevated: '#FFFFFF',
    surface: '#FFFFFF',         // Card surface
    surfaceElevated: '#FFFFFF',
    surfaceHover: '#F4F4F5',    // zinc-100
    surfaceBorder: '#E4E4E7',   // zinc-200

    textPrimary: '#18181B',     // Dark text (zinc-900)
    textSecondary: '#52525B',   // zinc-600
    textMuted: '#A1A1AA',       // zinc-400

    border: '#E4E4E7',          // zinc-200
    borderLight: '#D4D4D8',     // zinc-300

    sidebarBg: '#FFFFFF',
    sidebarHover: '#F4F4F5',
    sidebarActive: 'rgba(59, 130, 246, 0.08)',
    sidebarBorder: '#E4E4E7',
    sidebarText: '#71717A',     // zinc-500
    sidebarTextActive: '#3B82F6',
  },

  // ==========================================================================
  // SEMANTIC COLORS
  // ==========================================================================

  // Success
  success: '#10B981',           // Emerald green
  successLight: '#34D399',
  successDark: '#059669',
  successMuted: 'rgba(16, 185, 129, 0.12)',
  successBg: 'rgba(16, 185, 129, 0.08)',

  // Warning
  warning: '#F59E0B',           // Amber
  warningLight: '#FBBF24',
  warningDark: '#D97706',
  warningMuted: 'rgba(245, 158, 11, 0.12)',
  warningBg: 'rgba(245, 158, 11, 0.08)',

  // Error
  error: '#EF4444',             // Red
  errorLight: '#F87171',
  errorDark: '#DC2626',
  errorMuted: 'rgba(239, 68, 68, 0.12)',
  errorBg: 'rgba(239, 68, 68, 0.08)',

  // Info
  info: '#3B82F6',              // Blue
  infoLight: '#60A5FA',
  infoDark: '#2563EB',
  infoMuted: 'rgba(59, 130, 246, 0.12)',
  infoBg: 'rgba(59, 130, 246, 0.08)',
};

// =============================================================================
// DERIVED THEME OBJECT
// Computed values used throughout the app
// =============================================================================

export const theme = {
  colors: palette,

  // Gradients
  gradients: {
    primary: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.accent} 100%)`,
    primarySubtle: `linear-gradient(135deg, ${palette.primaryMuted} 0%, ${palette.primaryMuted} 100%)`,
    accent: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.tertiary} 100%)`,
    surface: `linear-gradient(180deg, ${palette.surface} 0%, ${palette.background} 100%)`,
    glow: `radial-gradient(ellipse at center, ${palette.primaryMuted} 0%, transparent 70%)`,
    mesh: `
      radial-gradient(at 40% 20%, ${palette.primaryMuted} 0px, transparent 50%),
      radial-gradient(at 80% 0%, ${palette.accentLight}15 0px, transparent 50%),
      radial-gradient(at 0% 50%, ${palette.cyanMuted} 0px, transparent 50%),
      radial-gradient(at 80% 50%, ${palette.tertiaryMuted} 0px, transparent 50%),
      radial-gradient(at 0% 100%, ${palette.primaryMuted} 0px, transparent 50%)
    `,
    card: `linear-gradient(145deg, ${palette.surfaceElevated} 0%, ${palette.surface} 100%)`,
    cardHover: `linear-gradient(145deg, ${palette.surfaceHover} 0%, ${palette.surfaceElevated} 100%)`,
    hero: `linear-gradient(135deg, ${palette.background} 0%, ${palette.backgroundElevated} 100%)`,
  },

  // Shadows - Disabled for industrial flat aesthetic
  shadows: {
    xs: 'none',
    sm: 'none',
    md: 'none',
    lg: 'none',
    xl: 'none',
    primary: 'none',
    primaryGlow: 'none',
    accent: 'none',
    card: 'none',
    cardHover: 'none',
    inset: 'none',
  },

  // Border radius (scaled down ~80%)
  radius: {
    xs: '3px',
    sm: '5px',
    md: '6px',
    lg: '10px',
    xl: '13px',
    '2xl': '16px',
    '3xl': '19px',
    full: '9999px',
  },

  // Transitions
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '400ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    spring: '600ms cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  },

  // Blur effects
  blur: {
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
  },
};

// =============================================================================
// TYPOGRAPHY CONFIGURATION
// =============================================================================

export const typography = {
  fontFamily: {
    display: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    body: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", "Fira Code", "SF Mono", monospace',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  fontSize: {
    xs: '0.625rem',    // 10px (was 12px)
    sm: '0.6875rem',   // 11px (was 13px)
    base: '0.75rem',   // 12px (was 14px)
    md: '0.8125rem',   // 13px (was 16px)
    lg: '0.875rem',    // 14px (was 18px)
    xl: '1rem',        // 16px (was 20px)
    '2xl': '1.25rem',  // 20px (was 24px)
    '3xl': '1.5rem',   // 24px (was 30px)
    '4xl': '1.875rem', // 30px (was 36px)
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

// =============================================================================
// ANIMATION CONFIGURATION
// =============================================================================

export const animation = {
  duration: {
    fast: '150ms',
    normal: '250ms',
    slow: '400ms',
    slower: '600ms',
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    spring: 'cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  },
};

// =============================================================================
// SPACING CONFIGURATION
// =============================================================================

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '1px',
  1: '3px',
  1.5: '5px',
  2: '6px',
  2.5: '8px',
  3: '10px',
  4: '13px',
  5: '16px',
  6: '20px',
  8: '26px',
  10: '32px',
  12: '38px',
  16: '52px',
  20: '64px',
  24: '77px',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Add opacity to a hex color
 * @param hex - Hex color string (e.g., '#6366F1')
 * @param opacity - Opacity value from 0 to 1
 * @returns RGBA color string
 */
export const withOpacity = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get contrasting text color (white or dark) for a background
 * @param hex - Background hex color
 * @returns '#FFFFFF' or '#0F172A'
 */
export const getContrastText = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#0F172A' : '#FFFFFF';
};

/**
 * Lighten a hex color
 * @param hex - Hex color string
 * @param amount - Amount to lighten (0-100)
 */
export const lighten = (hex: string, amount: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + amount);
  const b = Math.min(255, (num & 0xff) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

/**
 * Darken a hex color
 * @param hex - Hex color string
 * @param amount - Amount to darken (0-100)
 */
export const darken = (hex: string, amount: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

export default theme;
